import { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { submitQuote } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, ArrowLeft, ArrowRight, X } from "lucide-react";
import { Link } from "react-router-dom";
import { track } from "@/lib/analytics";

// Prioritized, quote-eligible categories. Order matters — keeps the chooser calm.
const PRIORITY_CATEGORIES = [
    "Roofing",
    "Foundation",
    "Structural",
    "Plumbing",
    "Electrical",
    "HVAC",
    "Water intrusion",
    "Drainage",
    "Mold/moisture",
];

const CONSENT_TEXT =
    "I agree to be contacted about my quote request by Buyer's Leverage and/or relevant repair service providers.";

const MAX_STEP = 4;  // 0..4 → Step 1..5

function StepDots({ step }) {
    return (
        <div className="flex items-center gap-1.5" data-testid="quote-stepper">
            {[0, 1, 2, 3, 4].map((i) => (
                <span
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                        i <= step ? "w-6 bg-bl-primary" : "w-3 bg-bl-soft"
                    }`}
                />
            ))}
        </div>
    );
}

function EligibleCategoriesFromReport(report) {
    if (!report?.full_summary) return [];
    const issues = report.full_summary.issues || [];
    const cats = new Set();
    issues.forEach((i) => {
        const c = i.category;
        if (!c) return;
        // Map free-form categories to prioritized canonical labels when possible.
        const lower = c.toLowerCase();
        if (lower.includes("roof")) cats.add("Roofing");
        else if (lower.includes("foundation")) cats.add("Foundation");
        else if (lower.includes("structural")) cats.add("Structural");
        else if (lower.includes("plumb")) cats.add("Plumbing");
        else if (lower.includes("electric")) cats.add("Electrical");
        else if (lower.includes("hvac")) cats.add("HVAC");
        else if (lower.includes("water") || lower.includes("moisture")) cats.add("Water intrusion");
        else if (lower.includes("drainage")) cats.add("Drainage");
        else if (lower.includes("mold")) cats.add("Mold/moisture");
        else cats.add(c);  // keep unknown categories as-is
    });
    return Array.from(cats);
}

function buildIssueSummary(report, selectedCategories, preselectedIssue) {
    const issues = report?.full_summary?.issues || [];
    const wanted = new Set(selectedCategories.map((c) => c.toLowerCase()));
    const summary = [];
    if (preselectedIssue) {
        summary.push({
            issue_id: preselectedIssue.issue_id,
            title: preselectedIssue.title,
            category: preselectedIssue.category,
            severity: preselectedIssue.severity,
            range_low: preselectedIssue.estimated_repair_range_usd?.low,
            range_high: preselectedIssue.estimated_repair_range_usd?.high,
        });
        return summary;
    }
    issues.forEach((iss) => {
        const cat = (iss.category || "").toLowerCase();
        // crude match: any selected category substring lands the issue
        const matched = Array.from(wanted).some((w) =>
            cat.includes(w) || w.includes(cat) || w === cat,
        );
        if (matched) {
            summary.push({
                issue_id: iss.issue_id,
                title: iss.title,
                category: iss.category,
                severity: iss.severity,
                range_low: iss.estimated_repair_range_usd?.low,
                range_high: iss.estimated_repair_range_usd?.high,
            });
        }
    });
    return summary.slice(0, 25);  // cap payload size — privacy + safety
}

export default function QuoteRequestModal({
    open,
    onClose,
    report,
    reportId,
    preselectedIssue,        // {issue_id, title, category, severity, estimated_repair_range_usd}
    sourceLocation = "report-overview",
}) {
    const { user } = useAuth();
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [doneQuoteId, setDoneQuoteId] = useState(null);

    // Categories from this report (preferred) + a few priority defaults
    const reportCategories = useMemo(() => EligibleCategoriesFromReport(report), [report]);
    const availableCategories = useMemo(() => {
        const fromReport = reportCategories.filter((c) => PRIORITY_CATEGORIES.includes(c) || true);
        const merged = Array.from(new Set([...fromReport, ...PRIORITY_CATEGORIES]));
        return merged.slice(0, 12);
    }, [reportCategories]);

    const [selected, setSelected] = useState(() => {
        if (preselectedIssue?.category) return [preselectedIssue.category];
        return [];
    });
    const [form, setForm] = useState(() => ({
        name: user?.name || "",
        email: user?.email || "",
        phone: "",
        preferred_contact_method: "email",
        best_time_to_contact: "anytime",
        property_address: report?.property_address || "",
        property_city: "",
        property_state: "",
        zip_code: report?.zip_code || "",
        user_notes: "",
        timeline: "",
        under_contract: null,
    }));
    const [consent, setConsent] = useState(false);

    // Reset state whenever modal opens
    useEffect(() => {
        if (!open) return;
        setStep(0);
        setSubmitting(false);
        setDone(false);
        setDoneQuoteId(null);
        setConsent(false);
        setSelected(preselectedIssue?.category ? [preselectedIssue.category] : []);
        setForm((prev) => ({
            ...prev,
            name: user?.name || "",
            email: user?.email || "",
            property_address: report?.property_address || "",
            zip_code: report?.zip_code || "",
        }));
        track("quote_flow_started", {
            report_id: reportId,
            source: sourceLocation,
            preselected: !!preselectedIssue,
        });
    }, [open, user?.email, user?.name, report?.property_address, report?.zip_code, reportId, sourceLocation, preselectedIssue]);

    const toggleCategory = (cat) => {
        setSelected((prev) =>
            prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
        );
        track("quote_category_selected", { category: cat });
    };

    const next = () => setStep((s) => Math.min(s + 1, MAX_STEP));
    const back = () => setStep((s) => Math.max(s - 1, 0));

    const canContinueFrom = {
        0: selected.length > 0,
        1: selected.length > 0,
        2: form.name.trim().length >= 2 && form.email.includes("@") && form.zip_code.length === 5,
        3: consent,
    };

    const handleSubmit = async () => {
        if (!consent) {
            toast.error("Please confirm consent to submit your quote request.");
            return;
        }
        try {
            setSubmitting(true);
            const payload = {
                selected_categories: selected,
                selected_issue_ids: preselectedIssue ? [preselectedIssue.issue_id] : [],
                issue_summary: buildIssueSummary(report, selected, preselectedIssue),
                report_id: reportId || null,
                upload_id: report?.upload_id || null,
                name: form.name,
                email: form.email,
                phone: form.phone || null,
                preferred_contact_method: form.preferred_contact_method,
                best_time_to_contact: form.best_time_to_contact,
                property_address: form.property_address || null,
                property_city: form.property_city || null,
                property_state: form.property_state || null,
                zip_code: form.zip_code || null,
                user_notes: form.user_notes || null,
                timeline: form.timeline || null,
                under_contract: form.under_contract,
                consent_given: true,
                consent_text: CONSENT_TEXT,
                source_location: sourceLocation,
            };
            const r = await submitQuote(payload);
            track("quote_request_submitted", {
                report_id: reportId,
                category_count: selected.length,
                source: sourceLocation,
            });
            setDoneQuoteId(r.quote_id);
            setDone(true);
            setStep(MAX_STEP);
        } catch (e) {
            const detail = e?.response?.data?.detail || "Could not submit quote request.";
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                className="max-w-2xl bg-bl-surface border-bl"
                data-testid="quote-request-modal"
            >
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <div className="bl-eyebrow w-fit">Get local quotes</div>
                        <StepDots step={step} />
                    </div>
                    <DialogTitle className="font-display text-2xl text-bl mt-2">
                        {done
                            ? "Quote request submitted"
                            : step === 0
                                ? "Which repairs would you like quotes for?"
                                : step === 1
                                    ? "Request quotes for selected repair categories"
                                    : step === 2
                                        ? "How should providers reach you?"
                                        : "Privacy & consent"}
                    </DialogTitle>
                    <DialogDescription className="text-bl-muted">
                        {done
                            ? "We've saved your request. You can review its status from your dashboard."
                            : step === 3
                                ? "You stay in control of what gets shared."
                                : "Calm. Optional. Privacy-conscious. You're in control."}
                    </DialogDescription>
                </DialogHeader>

                {/* STEP 1: Issue selection */}
                {!done && step === 0 && (
                    <div className="space-y-4" data-testid="quote-step-categories">
                        {preselectedIssue ? (
                            <div className="bl-card p-4 bg-bl-bg/40">
                                <div className="bl-eyebrow w-fit">Selected from issue card</div>
                                <div className="mt-2 font-display font-semibold text-bl">
                                    {preselectedIssue.title}
                                </div>
                                <div className="mt-1 text-xs text-bl-muted">
                                    {preselectedIssue.category} · severity {preselectedIssue.severity}
                                </div>
                                {preselectedIssue.estimated_repair_range_usd && (
                                    <div className="mt-2 font-mono-bl text-xs text-bl">
                                        Estimated repair exposure: ${preselectedIssue.estimated_repair_range_usd.low?.toLocaleString()} – ${preselectedIssue.estimated_repair_range_usd.high?.toLocaleString()}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-bl-muted text-sm">
                                Pick one or more categories. Add only what you actually want quotes for —
                                you can come back anytime.
                            </p>
                        )}

                        <div className="flex flex-wrap gap-2">
                            {availableCategories.map((cat) => {
                                const on = selected.includes(cat);
                                return (
                                    <button
                                        key={cat}
                                        onClick={() => toggleCategory(cat)}
                                        className={`px-3 py-1.5 text-sm rounded-full border transition ${
                                            on
                                                ? "bg-bl-primary text-bl-paper border-bl-primary"
                                                : "bg-bl-surface text-bl border-bl hover:border-bl-primary"
                                        }`}
                                        data-testid={`quote-category-${cat.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                                    >
                                        {on && <CheckCircle2 className="inline w-3.5 h-3.5 mr-1.5" />}
                                        {cat}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* STEP 2: Confirmation */}
                {!done && step === 1 && (
                    <div className="space-y-4" data-testid="quote-step-confirm">
                        <p className="text-bl-muted text-sm">
                            We'll use the issue summary from your report to help route your request.
                            You stay in control of what gets shared.
                        </p>
                        <div className="space-y-2">
                            {selected.map((cat) => (
                                <div
                                    key={cat}
                                    className="bl-card p-3 flex items-center justify-between"
                                    data-testid={`quote-confirm-${cat.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                                >
                                    <span className="text-bl text-sm font-medium">{cat}</span>
                                    <button
                                        onClick={() => toggleCategory(cat)}
                                        className="text-bl-muted hover:text-[var(--bl-rust)] p-1"
                                        aria-label={`Remove ${cat}`}
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* STEP 3: Contact + property */}
                {!done && step === 2 && (
                    <div className="space-y-3" data-testid="quote-step-contact">
                        <div className="grid grid-cols-2 gap-3">
                            <input
                                placeholder="Name *"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="bl-input"
                                data-testid="quote-input-name"
                            />
                            <input
                                placeholder="Email *"
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="bl-input"
                                data-testid="quote-input-email"
                            />
                            <input
                                placeholder="Phone"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                className="bl-input"
                                data-testid="quote-input-phone"
                            />
                            <input
                                placeholder="ZIP code *"
                                maxLength={5}
                                value={form.zip_code}
                                onChange={(e) => setForm({ ...form, zip_code: e.target.value.replace(/\D/g, "") })}
                                className="bl-input"
                                data-testid="quote-input-zip"
                            />
                            <input
                                placeholder="City"
                                value={form.property_city}
                                onChange={(e) => setForm({ ...form, property_city: e.target.value })}
                                className="bl-input"
                                data-testid="quote-input-city"
                            />
                            <input
                                placeholder="State"
                                maxLength={2}
                                value={form.property_state}
                                onChange={(e) => setForm({ ...form, property_state: e.target.value.toUpperCase() })}
                                className="bl-input"
                                data-testid="quote-input-state"
                            />
                            <select
                                value={form.preferred_contact_method}
                                onChange={(e) => setForm({ ...form, preferred_contact_method: e.target.value })}
                                className="bl-input"
                                data-testid="quote-input-method"
                            >
                                <option value="email">Email me</option>
                                <option value="phone">Call me</option>
                                <option value="text">Text me</option>
                            </select>
                            <select
                                value={form.best_time_to_contact}
                                onChange={(e) => setForm({ ...form, best_time_to_contact: e.target.value })}
                                className="bl-input"
                                data-testid="quote-input-time"
                            >
                                <option value="anytime">Anytime</option>
                                <option value="morning">Morning</option>
                                <option value="afternoon">Afternoon</option>
                                <option value="evening">Evening</option>
                            </select>
                            <input
                                placeholder="Property address (optional)"
                                value={form.property_address}
                                onChange={(e) => setForm({ ...form, property_address: e.target.value })}
                                className="bl-input col-span-2"
                                data-testid="quote-input-address"
                            />
                            <select
                                value={form.timeline}
                                onChange={(e) => setForm({ ...form, timeline: e.target.value })}
                                className="bl-input"
                                data-testid="quote-input-timeline"
                            >
                                <option value="">Timeline (optional)</option>
                                <option value="asap">ASAP / urgent</option>
                                <option value="2_weeks">Within 2 weeks</option>
                                <option value="1_month">Within 1 month</option>
                                <option value="exploring">Just exploring</option>
                            </select>
                            <select
                                value={form.under_contract === null ? "" : String(form.under_contract)}
                                onChange={(e) => setForm({
                                    ...form,
                                    under_contract: e.target.value === "" ? null : e.target.value === "true",
                                })}
                                className="bl-input"
                                data-testid="quote-input-contract"
                            >
                                <option value="">Under contract? (optional)</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                            <textarea
                                placeholder="Notes for providers (optional)"
                                value={form.user_notes}
                                onChange={(e) => setForm({ ...form, user_notes: e.target.value })}
                                rows={2}
                                className="bl-input col-span-2"
                                data-testid="quote-input-notes"
                            />
                        </div>
                    </div>
                )}

                {/* STEP 4: Privacy + consent */}
                {!done && step === 3 && (
                    <div className="space-y-4" data-testid="quote-step-consent">
                        <div className="bl-card p-4 bg-bl-bg/40">
                            <div className="flex items-start gap-2">
                                <ShieldCheck className="w-4 h-4 text-bl-primary mt-0.5 flex-shrink-0" />
                                <p className="text-bl text-sm leading-relaxed">
                                    We only share the information needed to process your quote request.
                                    Your full inspection report is <b>not</b> shared by default — only the
                                    selected categories and a brief summary of the related issues.
                                </p>
                            </div>
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="quote-consent-label">
                            <input
                                type="checkbox"
                                checked={consent}
                                onChange={(e) => setConsent(e.target.checked)}
                                className="mt-1"
                                data-testid="quote-consent-checkbox"
                            />
                            <span className="text-bl text-sm leading-relaxed">
                                {CONSENT_TEXT}
                            </span>
                        </label>
                    </div>
                )}

                {/* DONE */}
                {done && (
                    <div className="space-y-4" data-testid="quote-step-done">
                        <div className="bl-card p-5 bg-bl-bg/40 flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-bl-low flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-bl">
                                <div className="font-medium">Reference</div>
                                <div className="font-mono-bl text-xs mt-1">{doneQuoteId}</div>
                            </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <button
                                className="bl-btn-secondary"
                                onClick={onClose}
                                data-testid="quote-done-back"
                            >
                                Back to report
                            </button>
                            <Link
                                to="/account?tab=quotes"
                                className="bl-btn-primary"
                                data-testid="quote-done-view"
                                onClick={onClose}
                            >
                                View quote requests
                            </Link>
                        </div>
                    </div>
                )}

                {/* Footer nav */}
                {!done && (
                    <div className="flex items-center justify-between pt-2 border-t border-bl">
                        <button
                            onClick={step === 0 ? onClose : back}
                            className="bl-btn-ghost flex items-center gap-1.5 text-sm"
                            data-testid="quote-back-btn"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" />
                            {step === 0 ? "Cancel" : "Back"}
                        </button>
                        {step < 3 ? (
                            <button
                                disabled={!canContinueFrom[step]}
                                onClick={next}
                                className="bl-btn-primary flex items-center gap-1.5"
                                data-testid="quote-next-btn"
                            >
                                Continue
                                <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        ) : (
                            <button
                                disabled={!canContinueFrom[step] || submitting}
                                onClick={handleSubmit}
                                className="bl-btn-primary"
                                data-testid="quote-submit-btn"
                            >
                                {submitting ? "Submitting…" : "Submit request"}
                            </button>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
